import React, { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import { motion, AnimatePresence, useAnimation, useInView } from 'framer-motion';
import {
    Chat as MessageCircleIcon,
    Settings as SettingsIcon,
    People as UsersIcon,
    FormatQuote as QuoteIcon,
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';

// Styles
const styles = {
    section: {
        position: 'relative',
        overflow: 'hidden',
        py: { xs: 6, lg: 8 },
        width: '100%',
    },
    container: {
        maxWidth: '1280px',
        px: { xs: 2, sm: 3, lg: 4 },
    },
    accentColor: '#00C2FF',
    accentBlue: '#1f35cb',
    primaryBtn: {
        backgroundColor: '#000',
        color: '#fff',
        '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
        },
        px: 4,
        py: 1.5,
        fontSize: '1.125rem',
        textTransform: 'none',
    },
};

// ============ HERO SECTION ============
function FansHero() {
    const [activeIndex, setActiveIndex] = useState(0);
    const conversations = [
        "How can I improve my public speaking?",
        "What's the best way to start investing?",
        "Can you help with my workout routine?",
        "How do I start a successful business?",
        "Give me tips for better time management",
        "How do I learn programming?",
    ];

    useEffect(() => {
        const timer = setInterval(() => {
            setActiveIndex((prev) => (prev + 1) % conversations.length);
        }, 3000);
        return () => clearInterval(timer);
    }, []);

    return (
        <Box
            component="section"
            sx={{
                position: 'relative',
                background: 'linear-gradient(to bottom, white, rgba(239, 246, 255, 0.2))',
                pt: 4,
                pb: 4,
                overflow: 'hidden',
            }}
        >
            <Container sx={styles.container}>
                <Box sx={{ maxWidth: '768px', mx: 'auto', textAlign: 'center', position: 'relative', zIndex: 10 }}>
                    <Typography
                        variant="h2"
                        sx={{
                            fontSize: { xs: '2rem', sm: '3rem', md: '3.75rem' },
                            fontWeight: 700,
                            letterSpacing: '-0.02em',
                        }}
                    >
                        Your Personal
                        <br />
                        <Box
                            component="span"
                            sx={{
                                background: 'linear-gradient(to right, #00C2FF, #1f35cb)',
                                backgroundClip: 'text',
                                WebkitBackgroundClip: 'text',
                                color: 'transparent',
                            }}
                        >
                            AI Experts
                        </Box>
                    </Typography>
                    <Typography sx={{ mt: 2, fontSize: '1.125rem', color: 'text.secondary', maxWidth: '42rem', mx: 'auto' }}>
                        Get instant, personalized advice from AI personas trained by world-class experts. Available 24/7 to help you grow.
                    </Typography>

                    {/* Chat bubble animation */}
                    <Box sx={{ height: 100, my: 4, position: 'relative' }}>
                        {conversations.map((text, index) => (
                            <Box
                                key={index}
                                sx={{
                                    position: 'absolute',
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    width: '100%',
                                    transition: 'all 0.5s ease',
                                    opacity: index === activeIndex ? 1 : 0,
                                    translateY: index === activeIndex ? 0 : 32,
                                    pointerEvents: index === activeIndex ? 'auto' : 'none',
                                }}
                            >
                                <Box sx={{ display: 'flex', gap: 1.5, maxWidth: '28rem', mx: 'auto' }}>
                                    <Box
                                        sx={{
                                            width: 32,
                                            height: 32,
                                            borderRadius: '50%',
                                            backgroundColor: '#000',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0,
                                        }}
                                    >
                                        <MessageCircleIcon sx={{ fontSize: 16, color: 'white' }} />
                                    </Box>
                                    <Box
                                        sx={{
                                            flex: 1,
                                            backgroundColor: 'rgba(255,255,255,0.9)',
                                            backdropFilter: 'blur(4px)',
                                            borderRadius: 4,
                                            p: 2,
                                            boxShadow: 1,
                                        }}
                                    >
                                        <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'left' }}>
                                            {text}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        ))}
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 4 }}>
                        <Button variant="contained" sx={styles.primaryBtn} href="/creators" startIcon={<MessageCircleIcon />}>
                            Start Chatting
                        </Button>
                        <Button
                            variant="outlined"
                            href="#experts"
                            sx={{
                                borderColor: 'divider',
                                px: 4,
                                py: 1.5,
                                fontSize: '1.125rem',
                                textTransform: 'none',
                                '&:hover': { backgroundColor: 'action.hover' },
                            }}
                        >
                            Meet the Experts
                        </Button>
                    </Box>
                </Box>
            </Container>
        </Box>
    );
}

// ============ PARTNERS SECTION ============
const partners = [
    { name: 'Stanford University', logo: 'https://wevcmvkclihorrkofawh.supabase.co/storage/v1/object/public/logos/stanford.png' },
    { name: 'MIT', logo: 'https://wevcmvkclihorrkofawh.supabase.co/storage/v1/object/public/logos/mit.png' },
    { name: 'LinkedIn', logo: 'https://wevcmvkclihorrkofawh.supabase.co/storage/v1/object/public/logos/linkedin.jpeg' },
    { name: 'Meta', logo: 'https://wevcmvkclihorrkofawh.supabase.co/storage/v1/object/public/logos/meta.png' },
    { name: 'Microsoft', logo: 'https://wevcmvkclihorrkofawh.supabase.co/storage/v1/object/public/logos/microsoft.png' },
    { name: 'University of Cambridge', logo: 'https://wevcmvkclihorrkofawh.supabase.co/storage/v1/object/public/logos/cambridge.png' },
    { name: 'Harvard University', logo: 'https://wevcmvkclihorrkofawh.supabase.co/storage/v1/object/public/logos/harvard.png' },
    { name: 'Emmy Awards', logo: 'https://wevcmvkclihorrkofawh.supabase.co/storage/v1/object/public/logos/emmys.png' },
    { name: 'P&G', logo: 'https://wevcmvkclihorrkofawh.supabase.co/storage/v1/object/public/logos/pg.png' },
    { name: 'AWS', logo: 'https://wevcmvkclihorrkofawh.supabase.co/storage/v1/object/public/logos/aws.png' },
    { name: 'Netflix', logo: 'https://wevcmvkclihorrkofawh.supabase.co/storage/v1/object/public/logos/netflix.png' },
    { name: 'Deloitte', logo: 'https://wevcmvkclihorrkofawh.supabase.co/storage/v1/object/public/logos/deloitte.png' },
];

function PartnersSection() {
    const controls = useAnimation();
    const ref = useRef(null);
    const inView = useInView(ref);

    useEffect(() => {
        if (inView) {
            controls.start('visible');
        }
    }, [controls, inView]);

    return (
        <Box component="section" ref={ref} sx={{ ...styles.section, py: 12 }}>
            <Box
                sx={{
                    position: 'absolute',
                    inset: 0,
                    background: 'linear-gradient(to bottom, rgba(255,255,255,0.7), rgba(239, 246, 255, 0.3))',
                }}
            />
            <Container sx={{ ...styles.container, position: 'relative' }}>
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={controls}
                    variants={{ visible: { opacity: 1, y: 0 } }}
                >
                    <Typography
                        variant="h4"
                        sx={{
                            textAlign: 'center',
                            fontWeight: 500,
                            fontStyle: 'italic',
                            fontSize: { xs: '1.5rem', sm: '1.875rem', lg: '2.25rem' },
                            letterSpacing: '-0.02em',
                            mb: 8,
                        }}
                    >
                        Industry-leading creators, educators, and experts from
                    </Typography>
                </motion.div>

                {/* Logo marquee */}
                <Box
                    sx={{
                        overflow: 'hidden',
                        position: 'relative',
                        '&::before, &::after': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            bottom: 0,
                            width: 100,
                            zIndex: 1,
                        },
                        '&::before': {
                            left: 0,
                            background: 'linear-gradient(to right, white, transparent)',
                        },
                        '&::after': {
                            right: 0,
                            background: 'linear-gradient(to left, white, transparent)',
                        },
                    }}
                >
                    <motion.div
                        animate={{
                            x: [0, -50 * partners.length],
                        }}
                        transition={{
                            x: {
                                repeat: Infinity,
                                repeatType: 'loop',
                                duration: 50,
                                ease: 'linear',
                            },
                        }}
                        style={{ display: 'flex' }}
                    >
                        {[...partners, ...partners].map((partner, index) => (
                            <Box
                                key={`${partner.name}-${index}`}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    px: 2,
                                    width: 200,
                                    flexShrink: 0,
                                }}
                            >
                                <Box
                                    component="img"
                                    src={partner.logo}
                                    alt={partner.name}
                                    sx={{ height: 48, width: 'auto', objectFit: 'contain' }}
                                />
                            </Box>
                        ))}
                    </motion.div>
                </Box>
            </Container>
        </Box>
    );
}

// ============ FANS SECTION ============
const fanFeatures = [
    {
        icon: MessageCircleIcon,
        title: 'Instant Answers',
        description: "Get on-demand guidance, insight, and advice from experts you admire—no scheduling, no delays.",
    },
    {
        icon: SettingsIcon,
        title: 'Personalized Learning',
        description: 'Ask questions tailored to your interests and receive responses that adapt to your needs over time.',
    },
    {
        icon: UsersIcon,
        title: 'Explore New Perspectives',
        description: 'Discover diverse voices, broaden your understanding, and find inspiration whenever curiosity strikes.',
    },
];

function FansSection() {
    return (
        <Box component="section" sx={{ ...styles.section, py: 12 }}>
            <Container sx={styles.container}>
                <Grid container spacing={8} alignItems="center">
                    <Grid item xs={12} lg={6}>
                        <Typography sx={{ color: '#0077CC', fontSize: '1.25rem', mb: 2 }}>For Fans</Typography>
                        <Typography
                            variant="h3"
                            sx={{
                                fontSize: { xs: '2.5rem', sm: '3rem' },
                                fontWeight: 700,
                                lineHeight: 1.1,
                                mb: 8,
                            }}
                        >
                            Unlock Instant Guidance
                            <br />
                            from Trusted Voices
                        </Typography>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {fanFeatures.map((feature) => (
                                <Box key={feature.title} sx={{ display: 'flex', gap: 3 }}>
                                    <Box sx={{ flexShrink: 0 }}>
                                        <Box
                                            sx={{
                                                width: 48,
                                                height: 48,
                                                backgroundColor: '#E6F8FF',
                                                borderRadius: 2,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <feature.icon sx={{ fontSize: 24, color: '#000' }} />
                                        </Box>
                                    </Box>
                                    <Box>
                                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                            {feature.title}
                                        </Typography>
                                        <Typography sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                                            {feature.description}
                                        </Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Box>

                        <Button
                            variant="contained"
                            sx={{ ...styles.primaryBtn, mt: 8 }}
                            href="/?createAccount=true"
                        >
                            Get Started For Free
                        </Button>
                    </Grid>

                    <Grid item xs={12} lg={6}>
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Box
                                component="img"
                                src="https://wevcmvkclihorrkofawh.supabase.co/storage/v1/object/public/creator-section/discovery.png"
                                alt="Soopra Interface Preview"
                                sx={{
                                    maxWidth: '100%',
                                    height: 'auto',
                                    borderRadius: 6,
                                    boxShadow: 6,
                                }}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
}

// ============ TESTIMONIALS SECTION ============
const testimonials = [
    {
        quote: "Ronjon's Soopra gives a much more concise answer, suggesting his own introductory AI course taught at Stanford in the evenings…From this comparison, the ChatGPT answer is like from a handbook, while the Soopra answer is really like Ronjon's personal answer.",
        author: "Atmaja Patil",
        role: "Research Assistant",
        image: "https://wevcmvkclihorrkofawh.supabase.co/storage/v1/object/public/testimonials/atmaja%20patil.png"
    },
    {
        quote: "Soopra seemed very personable, as if a real person was giving me this advice. On the other hand, the ChatGPT reply appeared wordy and bloated.",
        author: "Seamus Doyle",
        role: "Marketing Intern",
        image: "https://wevcmvkclihorrkofawh.supabase.co/storage/v1/object/public/testimonials/seamus%20doyle.jpeg"
    },
    {
        quote: "What amazes me is how the AI persona maintains a unique teaching style and humor. It's like having 24/7 access to expertise, but in a way that feels natural and engaging.",
        author: "Patricia Salvatierra",
        role: "Business Development",
        image: "https://wevcmvkclihorrkofawh.supabase.co/storage/v1/object/public/testimonials/patricia.jpeg"
    },
    {
        quote: "The difference is striking - while other AI gives generic advice, this feels like having a conversation with someone who truly understands your field.",
        author: "Sameer Samara",
        role: "Content Manager",
        image: "https://wevcmvkclihorrkofawh.supabase.co/storage/v1/object/public/testimonials/sameer.png"
    }
];

function FansTestimonials() {
    const [activeIndex, setActiveIndex] = useState(0);

    useEffect(() => {
        const timer = setInterval(() => {
            setActiveIndex((prev) => (prev + 1) % testimonials.length);
        }, 5000);
        return () => clearInterval(timer);
    }, []);

    return (
        <Box
            component="section"
            sx={{
                background: 'linear-gradient(to bottom, rgba(239, 246, 255, 0.2), white)',
                py: 8,
            }}
        >
            <Container sx={styles.container}>
                <Box sx={{ textAlign: 'center', mb: 6 }}>
                    <Typography
                        variant="h3"
                        sx={{
                            fontWeight: 700,
                            fontSize: { xs: '1.875rem', sm: '2.25rem', md: '3rem' },
                            letterSpacing: '-0.02em',
                        }}
                    >
                        What Our Users Say
                    </Typography>
                    <Typography sx={{ mt: 2, fontSize: '1.125rem', color: 'text.secondary', maxWidth: '42rem', mx: 'auto' }}>
                        Experience the difference of conversing with AI personas trained on real expert knowledge
                    </Typography>
                </Box>

                <Box sx={{ maxWidth: '900px', mx: 'auto', position: 'relative' }}>
                    <Box sx={{ position: 'relative', height: 400 }}>
                        {testimonials.map((testimonial, index) => (
                            <Box
                                key={index}
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    transition: 'all 0.5s ease',
                                    opacity: index === activeIndex ? 1 : 0,
                                    transform: index === activeIndex ? 'translateY(0)' : 'translateY(32px)',
                                    pointerEvents: index === activeIndex ? 'auto' : 'none',
                                }}
                            >
                                <Box
                                    sx={{
                                        background: 'linear-gradient(to bottom, white, rgba(239, 246, 255, 0.2))',
                                        p: 4,
                                        borderRadius: 4,
                                        boxShadow: 2,
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                                        <Box
                                            component="img"
                                            src={testimonial.image}
                                            alt={testimonial.author}
                                            sx={{
                                                width: 64,
                                                height: 64,
                                                borderRadius: '50%',
                                                objectFit: 'cover',
                                            }}
                                        />
                                        <Box>
                                            <Typography sx={{ fontWeight: 600, color: 'text.primary' }}>
                                                {testimonial.author}
                                            </Typography>
                                            <Typography sx={{ color: styles.accentBlue }}>
                                                {testimonial.role}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <QuoteIcon sx={{ fontSize: 40, color: styles.accentBlue, mb: 2 }} />
                                    <Typography sx={{ color: 'text.secondary', fontSize: '1.125rem' }}>
                                        {testimonial.quote}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>

                    {/* Navigation dots */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 4 }}>
                        {testimonials.map((_, index) => (
                            <Box
                                key={index}
                                component="button"
                                onClick={() => setActiveIndex(index)}
                                sx={{
                                    width: 12,
                                    height: 12,
                                    borderRadius: '50%',
                                    border: 'none',
                                    cursor: 'pointer',
                                    backgroundColor: index === activeIndex ? styles.accentBlue : '#e5e7eb',
                                    transition: 'background-color 0.3s ease',
                                }}
                            />
                        ))}
                    </Box>
                </Box>
            </Container>
        </Box>
    );
}

// ============ MAIN PAGE COMPONENT ============
export default function ForFansPage() {
    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
            <FansHero />
            <PartnersSection />
            <FansSection />
            <FansTestimonials />
        </Box>
    );
}
